import React from 'react';
import { Check } from '../FormControls';
import { SETTINGS_WORDING as W, holdDaysDraft } from '../../domain/studioSettings';
import { useCommittedValue, blurOnEnter } from './useCommitted';
import s from './settings.module.css';

// Business Settings → General → Event Scheduling.
//
// ── What MMS shows ──────────────────────────────────────────────────────────
//   Scheduling Conflicts
//   [x] Check for scheduling conflicts when adding/editing calendar events
//
//   Enable Multi-Time Zone Selection
//   [ ] Allow students to select a different time zone than yours
//       Use if you have remote students
//
// Multi-Time Zone Selection decides whether the forms ask for a family's zone
// (#47). It gates the control, not the data, as MMS does: with the box
// unticked there is no Time Zone field on any of its forms and no Time Zone
// column in its student list. A zone already stored is used on the
// family-facing surfaces whether or not this is ticked, so unticking hides a
// question rather than retracting an answer a family gave.
//
// It does not move a lesson, in MMS or here: a lesson is anchored in the zone
// its series was created in; a family's zone is for the messages addressed to
// them (src/domain/timeZone.js, #47).
//
// The field lives on the family, as MMS's form says under it, verbatim:
//
//     This time zone will also apply to all contacts and Students in this
//     student's Family.
//
//   * Add New Student → step 2 "Family Details" → Additional Details, the last
//     field before Parent Lesson Reminders. There is no zone field on step 1.
//   * The Edit Student drawer, as its first field — reachable from a student,
//     writing the family's.
//
// It also shows as a subtitle under the "Student Details" card heading
// ("Timezone: …") and as an opt-in column in the students list (FamilyStep.jsx,
// StudentDetailsTab.jsx).
//
// Two knowing differences: MMS has no "same as the studio" option — its
// dropdown always holds a concrete zone, pre-filled with the studio's — where
// we keep null as a real value, because a stored copy of a setting goes stale
// when the setting changes. And we list IANA identifiers rather than MMS's
// Windows display names (`timeZoneOptions` in lib/intlOptions.js).
//
// The hold duration ("Weekly time slot hold duration") is under Policies →
// Booking Policy in MMS and on our Policies tab (#766, PoliciesPanel.jsx), which
// is drawn only while the Family Portal is open (#769); until then the box is
// here (`withHold`). It is `scheduling.weeklyHoldDays` in both places, and
// commits on blur, because "14" passes through "1" on the way in.
//
// Saved on change like every surface in Business Settings (the top of
// SettingsPage.jsx). Every `onChange` emits only the field that changed: the
// provider merges a group patch into what is stored (GROUPS in
// data/useSettings.jsx), and `{ ...scheduling, ...patch }` would carry this
// render's copy of the other fields, so two quick changes faster than a
// snapshot round-trip would spring the first back.

export default function EventSchedulingPanel({ scheduling, onChange, withHold = false }) {
  // Parsed through the same normaliser the document is written with, so what the
  // field shows after a blur is exactly what was stored — an empty box or "999"
  // comes back as the clamped value rather than sitting there looking accepted.
  const hold = useCommittedValue(
    scheduling.weeklyHoldDays,
    (draft) => holdDaysDraft(draft),
    (weeklyHoldDays) => onChange({ weeklyHoldDays }),
  );

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>{W.eventScheduling}</div>

      <div className={s.group}>
        <div className={s.groupLabel}>{W.conflictsLabel}</div>
        <Check
          checked={scheduling.checkConflicts}
          onChange={(e) => onChange({ checkConflicts: e.target.checked })}
          label={W.conflictsCheck}
        />
      </div>

      <div className={s.group}>
        <div className={s.groupLabel}>{W.multiTimeZoneLabel}</div>
        <Check
          checked={scheduling.multiTimeZone}
          onChange={(e) => onChange({ multiTimeZone: e.target.checked })}
          label={W.multiTimeZoneCheck}
        />
        <div className={s.groupHelp}>
          {W.multiTimeZoneHelp}
        </div>
        {/* MMS's hint is the bare "Use if you have remote students." The second
            sentence is ours: whether ticking it is about to move the calendar. */}
      </div>

      {withHold ? (
        <div className={s.group}>
          <label className={s.groupLabel} htmlFor="weekly-hold-days">
            {W.holdLabel}
          </label>
          <div className={s.numberRow}>
            <input
              id="weekly-hold-days"
              type="number"
              min="1"
              max="60"
              className={s.numberInput}
              {...hold.field}
              onKeyDown={blurOnEnter}
            />
            <span className={s.unit}>{W.holdUnit}</span>
          </div>
          <div className={s.groupHelp}>
            {W.holdHelp}
          </div>
        </div>
      ) : null}
    </div>
  );
}
